import { Box, InputAdornment, styled } from '@mui/material';
import { Search } from '@styled-icons/bootstrap';
import { Close } from '@styled-icons/evaicons-solid/Close';
import TraceableLocation from 'assets/img/cards/traceable-illustration.png';
import Filter from 'Components/Filter/Filter';
import { TFilter, TFilterEvent } from 'Components/Filter/filter.types';
import Loader from 'Components/Forms/Loader';
import { SearchInput } from 'Components/Forms/SearchToolbar';
import { ThemeTypography } from 'DesignSystem';
import React, { FC } from 'react';
import { IActivity } from 'FeatureModules/Activity/types/activity.types';
import ActivityRow from './ActivityRow';
import { CloseButton } from '../../../Partner/Components/Partners.styles';
import InfiniteScrollWrapper from 'Components/InfiniteScrollWrapper';

interface IActivitySelectionProps {
  activities: IActivity[];
  selectedActivityId?: string;
  filters: TFilter[];
  loading?: boolean;
  hasNextPage: boolean;
  searchTerm?: string | null;
  onSearchTermChange: (term: string) => void;
  onPageEndReached: () => void;
  onSelectionChange: (activityId?: string) => void;
  onFilterChange: (event: TFilterEvent) => void;
  onFilterReset: () => void;
}

const IllustrationWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: theme.spacing(1, 4),
}));

const Illustration = styled('img')(({ theme }) => ({
  width: 120,
}));

const Searchbar = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
}));

const ScrollContainer = styled(Box)({
  width: '100%',
  zIndex: 2,
  paddingBottom: 88,
});

const ActivitySelection: FC<IActivitySelectionProps> = ({
  activities,
  selectedActivityId,
  filters,
  hasNextPage,
  loading,
  searchTerm,
  onSearchTermChange,
  onPageEndReached,
  onSelectionChange,
  onFilterChange,
  onFilterReset,
}) => {
  const toggleActivity = (activity: IActivity) => {
    if (activity.id === selectedActivityId) {
      onSelectionChange(undefined);
    } else {
      onSelectionChange(activity.id);
    }
  };

  return (
    <Box width="100%">
      <Filter filters={filters} onFilterChange={onFilterChange} onFilterReset={onFilterReset} />

      <Searchbar>
        <SearchInput
          setDebouncedState={onSearchTermChange}
          className="search-input"
          delay={200}
          placeholder="Search activities"
          value={searchTerm}
          inputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {searchTerm === '' ? (
                  <Search size={12} />
                ) : (
                  <CloseButton onClick={() => onSearchTermChange('')}>
                    <Close size={16} />
                  </CloseButton>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Searchbar>

      {activities.length > 0 && !loading && (
        <ScrollContainer>
          <InfiniteScrollWrapper height={450} hasMore={hasNextPage} next={onPageEndReached}>
            {activities.map(activity => {
              return (
                <Box mt={1} key={`activity-block-${activity.id}`}>
                  <ActivityRow
                    isSelected={selectedActivityId === activity.id}
                    activity={activity}
                    hasShadow
                    onSelect={() => {
                      toggleActivity(activity);
                    }}
                  />
                </Box>
              );
            })}
          </InfiniteScrollWrapper>
        </ScrollContainer>
      )}

      {loading && (
        <Box position="relative" p={2} mt={10}>
          <Loader />
        </Box>
      )}

      {activities.length === 0 && !loading && (
        <Box mt={10}>
          <IllustrationWrapper>
            <Illustration src={TraceableLocation} alt="traceable illustration" />
            <Box mt={3}>
              <ThemeTypography variant="BODY_LARGER_BOLD" color="GRAY_60">
                No search results
              </ThemeTypography>
            </Box>
          </IllustrationWrapper>
        </Box>
      )}
    </Box>
  );
};

export default ActivitySelection;
